import type { Key } from "react";
import type { MenuProps } from "antd";
import { Dropdown, Flex, Typography } from "antd";
import {
  integrationProductActionMenuItems,
  isIntegrationProductAction,
  type IntegrationProductAction,
} from "../integrationProductActions";

const { Text } = Typography;

/** Same cap as previous `RowSelectionCounter` (`overflowCount={100}`). */
const SELECTION_COUNT_OVERFLOW = 100;

function formatSelectionCountLabel(count: number): string {
  if (count > SELECTION_COUNT_OVERFLOW) return `${SELECTION_COUNT_OVERFLOW}+`;
  return String(count);
}

/** Stable-width pill so “0” and “1+” don’t resize the bar (unlike data-grid RowSelectionCounter + Badge). */
function IntegrationSelectionCountPill({ count }: { count: number }) {
  const label = formatSelectionCountLabel(count);
  const hasSelection = count > 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        minWidth: 52,
        height: 32,
        padding: "0 12px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 500,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
        border: hasSelection ? "1px solid #b7eb8f" : "1px solid #dddddd",
        backgroundColor: hasSelection ? "#f6ffed" : "#f5f5f5",
        color: hasSelection ? "rgba(0, 0, 0, 0.88)" : "rgba(0, 0, 0, 0.45)",
      }}
    >
      {label}
    </span>
  );
}

export type IntegrationBulkActionsBarProps = {
  selectedCount: number;
  selectedRowKeys: Key[];
  onBulkAction?: (action: IntegrationProductAction, rowKeys: string[]) => void;
  className?: string;
};

/** Row selection count + bulk dropdown (no outer Card — use in table `title` or wrap yourself). */
export function IntegrationBulkActionsBar({
  selectedCount,
  selectedRowKeys,
  onBulkAction,
  className,
}: IntegrationBulkActionsBarProps) {
  const actionsDisabled = selectedCount === 0;
  const keysAsStrings = selectedRowKeys.map((k) => String(k));

  const menu: MenuProps = {
    items: integrationProductActionMenuItems ?? [],
    onClick: ({ key }) => {
      if (isIntegrationProductAction(key)) {
        onBulkAction?.(key, keysAsStrings);
      }
    },
  };

  return (
    <Flex
      justify="space-between"
      align="flex-end"
      gap={16}
      wrap="wrap"
      style={{ width: "100%" }}
      className={className}
    >
      <Flex vertical gap={6} style={{ flex: "0 1 auto", minWidth: 0 }}>
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
          Markerade artiklar
        </Text>
        <IntegrationSelectionCountPill count={selectedCount} />
      </Flex>
      <Flex vertical gap={6} align="flex-end" style={{ flex: "0 0 auto" }}>
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
          Åtgärd
        </Text>
        <Dropdown.Button
          menu={menu}
          disabled={actionsDisabled}
          onClick={() => onBulkAction?.("sync", keysAsStrings)}
        >
          Synkronisera
        </Dropdown.Button>
      </Flex>
    </Flex>
  );
}
